import { Component, inject, input, OnInit, signal } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { forkJoin, of } from "rxjs";

import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import type { WorkflowDiffVersionType } from "../../../../../../models";
import { MetricApiService, PropertiesService } from "../../../../../../services";
import { AccordionDiffViewComponent, ListDiffViewComponent, NormalDiffViewComponent } from "../../_lib";
import {
  AccordionSectionConfigType,
  CompositeSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";
import { monitoringMetricsScreenConfig } from "./metrics-screen-config";

@Component({
  selector: "adaa-metrics-screen",
  imports: [TranslatePipe, NormalDiffViewComponent, ListDiffViewComponent, AccordionDiffViewComponent],
  templateUrl: "./metrics-screen.html",
  styleUrl: "./metrics-screen.scss",
})
export class MetricsScreenComponent implements OnInit {
  private readonly _propertiesService = inject(PropertiesService);
  private readonly _metricsApiService = inject(MetricApiService);

  showOnlyNew = input.required<boolean>();
  isNew = input<boolean>(false);
  newObject = input.required<WorkflowDiffVersionType>();
  oldObject = input.required<WorkflowDiffVersionType>();
  viewOnlyDifferences = input<boolean>(false);

  isScreenReady = signal<boolean>(false);

  readonly $asListConfig = ($s: CompositeSectionConfigType) => $s as ListSectionConfigType;
  readonly $asAccordionConfig = ($s: CompositeSectionConfigType) => $s as AccordionSectionConfigType;

  config: {
    type: ScreenType;
    sections: (ScreenSectionConfigType | AccordionSectionConfigType)[];
  };

  public ngOnInit(): void {
    // TODO: If we need to support any other metric type we can add the config here
    // For now, we only support monitoring metrics
    this.config = monitoringMetricsScreenConfig;
    this._fetchConstants();
  }

  private _fetchConstants(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    forkJoin({
      dataTypes: this._propertiesService.getPropByIdList([
        Constants.CONSTANT_MEASUREMENT_UNIT_METRICS,
        Constants.CONSTANT_METRICS_MONTH_DIFFERENCE,
      ]),
      dashboardTypes: this._metricsApiService.getAvailableDashboardTypes(),
      parentMetricEntities: this._metricsApiService.getParentMetricEntities(),
      oldParentMetrics: this.oldObject()?.parentMetricEntityId
        ? this._metricsApiService.getParentMetrics(this.oldObject()?.parentMetricEntityId)
        : of(null),
      newParentMetrics:
        this.newObject()?.parentMetricEntityId &&
        this.newObject()?.parentMetricEntityId !== this.oldObject()?.parentMetricEntityId
          ? this._metricsApiService.getParentMetrics(this.newObject()?.parentMetricEntityId)
          : of(null),
    }).subscribe({
      next: (results) => {
        for (const section of this.config.sections) {
          if (section.view !== "normal" && section.view !== "list") continue;

          switch ((section as ScreenSectionConfigType).field) {
            case "dataType":
              (section as ScreenSectionConfigType).format = (data: { dataType: number }) =>
                that._getTextFromProp(data.dataType, results.dataTypes.responseData);
              break;

            case "monthDifference":
              (section as ScreenSectionConfigType).format = (data: { monthDifference: number }) =>
                that._getTextFromProp(data.monthDifference, results.dataTypes.responseData, "monthDifference");
              break;

            case "dashboardType":
              (section as ScreenSectionConfigType).format = (data: { dashboardType: number }) =>
                that._getTextFromProp(data.dashboardType, results.dashboardTypes.responseData);
              break;

            case "parentMetricEntityId":
              (section as ScreenSectionConfigType).format = (data: { parentMetricEntityId: number }) =>
                that._getTextFromProp(data?.parentMetricEntityId, results.parentMetricEntities.responseData);
              break;

            case "parentMetricId":
              (section as ScreenSectionConfigType).format = (data: {
                parentMetricEntityId: number;
                parentMetricId: number;
              }) => {
                const parentMetricEntityId = data?.parentMetricEntityId;
                if (!parentMetricEntityId) return "";

                const allMetrics = [
                  ...(results.oldParentMetrics?.responseData ?? []),
                  ...(results.newParentMetrics?.responseData ?? []),
                ];

                return that._getTextFromProp(data.parentMetricId, allMetrics);
              };

              break;

            default:
              break;
          }
        }

        this.isScreenReady.set(true);
      },
    });
  }

  private _getTextFromProp(id: number, list: { id: number; value?: string }[], key?: string): string {
    const data = list.find((e) => (key === "monthDifference" && e.value ? +e.value === id : e.id === id));

    return data ? AdaaHelper.getItemValueByToken(data, "name") : "";
  }
}
